import { CanvasTexture } from 'three';

export const ATLAS_SIZE = 2048;
export const FONT_SIZE = 64; // base font size used to render text into the atlas (actual size is scaled per panel)
const LINE_HEIGHT = FONT_SIZE + 8;
const MAX_ATLAS_HEIGHT = 16384;

/** Rounds n up to the next power of two (minimum 64). */
function getRoundedHeight(n: number): number {
    return Math.pow(2, Math.ceil(Math.log2(Math.max(n, 64))));
}

export interface Label {
    text: string;
    fontSize: number;
    maxWidth: number;
}

export interface RectUV {
    x: number; // left edge in atlas UV space [0..1]
    y: number; // top edge in atlas UV space [0..1]
    width: number;
    height: number;
}

function wrapText(context: CanvasRenderingContext2D, text: string, maxWidthPx: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let line = '';

    for (const word of words) {
        const text = line ? `${line} ${word}` : word;
        if (line && context.measureText(text).width > maxWidthPx) {
            lines.push(line);
            line = word;
        } else {
            line = text;
        }
    }

    if (line) {
        lines.push(line);
    }

    return lines;
}

/**
 * Builds a single atlas texture that includes multiple label texts.
 */
export function buildAtlas(labels: Label[]): {
    texture: CanvasTexture;
    uvRects: Map<string, RectUV>;
    atlasWidth: number;
    atlasHeight: number;
} {
    // UV rectangles that describe where each text label is located inside the atlas texture
    const uvRects = new Map<string, RectUV>();

    // unique label texts to render into the atlas texture (deduplicated by text + maxWidthPx)
    const atlasEntries = new Map<string, { text: string; maxWidthPx: number }>();

    // filter duplicated label texts by (text, maxWidthPx)
    for (const label of labels) {
        const maxWidthPx = Math.round((label.maxWidth / label.fontSize) * FONT_SIZE);
        const key = `${label.text}\0${maxWidthPx}`;
        if (!atlasEntries.has(key)) {
            atlasEntries.set(key, { text: label.text, maxWidthPx });
        }
    }

    // determine the required atlas height
    const testCanvas = document.createElement('canvas');
    testCanvas.width = ATLAS_SIZE;
    testCanvas.height = 1;
    const testCtx = testCanvas.getContext('2d')!;
    testCtx.font = `400 ${FONT_SIZE}px Arial`;

    type EntryLayout = { lines: string[]; pixelHeight: number; pixelWidth: number };
    const layout = new Map<string, EntryLayout>();
    let neededHeight = 0;

    for (const [key, { text, maxWidthPx }] of atlasEntries) {
        const lines = wrapText(testCtx, text, maxWidthPx);
        const pixelHeight = lines.length * LINE_HEIGHT - (LINE_HEIGHT - FONT_SIZE);
        const pixelWidth = Math.ceil(Math.max(...lines.map((l) => testCtx.measureText(l).width)));
        layout.set(key, { lines, pixelHeight, pixelWidth });
        neededHeight += pixelHeight + 16; // 16 px gap between entries
    }

    if (neededHeight > 0) {
        neededHeight -= 16;
    }

    const atlasWidth = ATLAS_SIZE;
    const atlasHeight = Math.min(getRoundedHeight(Math.max(neededHeight, 1)), MAX_ATLAS_HEIGHT);

    // create atlas canvas (WebGL cannot render texts directly)
    const canvas = document.createElement('canvas');
    canvas.width = atlasWidth;
    canvas.height = atlasHeight;

    const context = canvas.getContext('2d');
    if (context) {
        context.clearRect(0, 0, atlasWidth, atlasHeight);
        context.fillStyle = 'white';
        context.font = `400 ${FONT_SIZE}px Arial`;
        context.textBaseline = 'top';

        let y = 0;

        for (const [key, entry] of layout) {
            const { lines, pixelHeight, pixelWidth } = entry;

            for (let line = 0; line < lines.length; line++) {
                context.fillText(lines[line], 0, y + line * LINE_HEIGHT);
            }

            uvRects.set(key, {
                x: 0,
                y: y / atlasHeight,
                width: pixelWidth / atlasWidth,
                height: pixelHeight / atlasHeight
            });

            y += pixelHeight + 16; // 16 px gap between atlas entries
        }
    }

    const texture = new CanvasTexture(canvas);
    texture.needsUpdate = true;
    texture.flipY = false;

    return { texture, uvRects, atlasWidth, atlasHeight };
}
